import { useQuery, type UseQueryOptions } from "@tanstack/react-query";

import { getAllPages } from "@/api/helpers";
import type { PaginationParams, PaginationResponse } from "@/types";
import { cursorFromNextUrl, MAX_PAGE_SIZE } from "@/types/pagination";

export type PageOptions = {
  pageSize?: number;
  cursor?: string;
};

/** Per-hook react-query options, e.g. keepPreviousData. */
type PassthroughOptions<T> = Pick<
  UseQueryOptions<PaginationResponse<T>, Error, PaginationResponse<T>>,
  "placeholderData" | "enabled"
>;

/** One page of a cursor-paginated list, cached so mutations can invalidate it. */
export function usePage<T>(
  queryKey: unknown[],
  fetchPage: (params: PaginationParams) => Promise<PaginationResponse<T>>,
  options: PageOptions = {},
  queryOptions: PassthroughOptions<T> = {},
) {
  const { cursor, pageSize } = options;

  const query = useQuery({
    queryKey: [...queryKey, cursor, pageSize],
    queryFn: () => fetchPage({ cursor, page_size: pageSize }),
    ...queryOptions,
  });
  const nextCursor = cursorFromNextUrl(query.data?.next_url);

  return {
    data: query.data?.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: () => {
      void query.refetch();
    },
    items: query.data?.data ?? [],
    nextCursor,
    hasNextPage: query.data?.has_next === true && !!nextCursor,
  };
}

const EMPTY_ITEMS: never[] = [];

// Pickers show every option at once, so walk the whole cursor chain.
export function useAllPages<T>(
  queryKey: unknown[],
  getPage: (params: PaginationParams) => Promise<PaginationResponse<T>>,
  options: { enabled?: boolean } = {},
) {
  const query = useQuery({
    queryKey: [...queryKey, "all"],
    queryFn: () => getAllPages(getPage, MAX_PAGE_SIZE),
    enabled: options.enabled ?? true,
  });

  return {
    items: (query.data ?? EMPTY_ITEMS) as T[],
    isLoading: query.isLoading,
    error: query.error,
  };
}
